// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/TaskExecutionModal.jsx

import React, { useMemo, useState } from "react";
import ExecutionModalShell from "./shared/ExecutionModalShell";
import ExecutionStatusBadge from "./shared/ExecutionStatusBadge";
import TaskOverviewTab from "./tabs/TaskOverviewTab";
import RemediationGuidanceTab from "./tabs/RemediationGuidanceTab";
import WorkflowExecutionTab from "./tabs/WorkflowExecutionTab";
import ValidationExecutionTab from "./tabs/ValidationExecutionTab";
import EvidenceExecutionTab from "./tabs/EvidenceExecutionTab";
import SOPExecutionTab from "./tabs/SOPExecutionTab";
import HistoryExecutionTab from "./tabs/HistoryExecutionTab";
import DecisionSupportTab from "./tabs/DecisionSupportTab";
import OptimizationInsightPanel from "../work-optimization/OptimizationInsightPanel";
import ExecutionPlanTab from "../execution-plans/ExecutionPlanTab";
import LearningInsightPanel from "../learning/LearningInsightPanel";

const PRIMARY_TABS = [
  { key: "overview", label: "Overview" },
  { key: "guidance", label: "Guidance" },
  { key: "execution_plan", label: "Execution Plan" },
  { key: "workflow", label: "Workflow" },
  { key: "validation", label: "Validation" },
  { key: "evidence", label: "Evidence" },
];

const SUPPORTING_TABS = [
  { key: "learning", label: "Learning Insights" },
  { key: "optimization", label: "Optimization" },
  { key: "sop", label: "SOP" },
  { key: "history", label: "History" },
  { key: "decision", label: "Decision Support" },
];

const ALL_TABS = [...PRIMARY_TABS, ...SUPPORTING_TABS];

function TaskExecutionTabButton({ tab, active, onClick }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`task-execution-nav__tab${active ? " is-active" : ""}`}
      onClick={onClick}
    >
      {tab.label}
    </button>
  );
}

function displayValue(value, fallback = "—") {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((item) => displayValue(item, "")).filter(Boolean).join(", ") || fallback;
  if (typeof value === "object") {
    const candidate = value.label || value.title || value.name || value.value || value.status || value.state || value.id;
    if (candidate !== undefined && candidate !== null && candidate !== value) return displayValue(candidate, fallback);
    try { return JSON.stringify(value); } catch (error) { return fallback; }
  }
  return String(value);
}

function humanizeDisplayValue(value, fallback = "—") {
  const raw = displayValue(value, "").trim();
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    soc2: "SOC2",
    "soc 2": "SOC2",
    nist: "NIST",
    iso: "ISO",
    hipaa: "HIPAA",
    ccpa: "CCPA",
    aml: "AML",
    kyc: "KYC",
    fda: "FDA",
    osha: "OSHA",
    sop: "SOP",
    aws: "AWS",
    s3: "S3",
    iam: "IAM",
    waf: "WAF",
    api: "API",
    cloudflare: "Cloudflare",
    okta: "Okta",
    "google workspace": "Google Workspace",
    "risk queue": "Risk Queue",
    "risk queue default": "Risk Queue Default",
    "validation rerun": "Validation Rerun",
    "not linked": "Not Linked",
    "not loaded": "Not Loaded",
    "in progress": "In Progress",
    "ready for validation": "Ready for Validation",
    "ready to work": "Ready to Work",
    "awaiting validation": "Awaiting Validation",
    "awaiting approval": "Awaiting Approval",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return exactLabels[key];
      if (/^cc\d+(?:\.\d+)?$/i.test(word)) return word.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function titleForTask(task = {}) {
  return displayValue(task.title || task.summary || task.name || task.rule_id || task.finding_id, "Task Execution");
}

function subtitleForTask(task = {}) {
  const parts = [
    displayValue(task.framework || task.affected_framework, ""),
    displayValue(task.control_id || task.control, ""),
    humanizeDisplayValue(task.connector || task.affected_connector || task.provider, ""),
    displayValue(task.resource_id || task.resource, ""),
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "Task-scoped operational workbench";
}

export default function TaskExecutionModal({
  open,
  task,
  initialTab = "overview",
  onClose,
  remediationGuidanceContext = null,
  remediationGuidanceLoading = false,
  remediationGuidanceError = "",
  onRefreshRemediationGuidance,
  executionPlanContext = null,
  executionPlanLoading = false,
  executionPlanError = "",
  onRefreshExecutionPlan,
  learningContext = null,
  learningLoading = false,
  learningError = "",
  learningRefreshState = "idle",
  onRefreshLearning,
  intelligence = null,
  intelligenceLoading = false,
  intelligenceError = "",
  intelligenceQuestion = "",
  onRequestIntelligence,
  optimizationContext = null,
  optimizationLoading = false,
  optimizationError = "",
  onRefreshOptimization,
  decision,
  historyEvents = [],
  workflowEvents = [],
  workflowContext = null,
  workflowLoading = false,
  workflowError = "",
  onRefreshWorkflow,
  sopContext = null,
  sopLoading = false,
  sopError = "",
  onRefreshSOP,
  validationEvents = [],
  evidenceRecords = [],
  evidenceContext = null,
  evidenceLoading = false,
  evidenceError = "",
  onRefreshEvidence,
  onRunValidation,
  validationRunning = false,
  onRefreshDecision,
  onDecisionFeedback,
  decisionLoading = false,
}) {
  const [activeTab, setActiveTab] = useState(initialTab || "overview");

  React.useEffect(() => {
    if (open) setActiveTab(initialTab || "overview");
  }, [open, initialTab]);

  const activeTabConfig = useMemo(() => ALL_TABS.find((tab) => tab.key === activeTab) || ALL_TABS[0], [activeTab]);
  const safeTask = task && typeof task === "object" ? task : {};
  const safeHistoryEvents = Array.isArray(historyEvents) ? historyEvents : [];
  const safeWorkflowEvents = Array.isArray(workflowEvents) ? workflowEvents : [];
  const safeValidationEvents = Array.isArray(validationEvents) ? validationEvents : [];
  const safeEvidenceRecords = Array.isArray(evidenceRecords) ? evidenceRecords : [];

  if (!open || !task) return null;

  return (
    <ExecutionModalShell open={open} ariaLabel="Task Execution Workbench" eyebrow="Mission Execution Workbench" title={titleForTask(safeTask)} subtitle={subtitleForTask(safeTask)} onClose={onClose} maxWidth="1180px" accentBorder="rgba(96, 165, 250, 0.45)">
      <div className="task-execution-nav">
        <div
          className="task-execution-nav__primary"
          role="tablist"
          aria-label="Primary task execution views"
        >
          {PRIMARY_TABS.map((tab) => (
            <TaskExecutionTabButton
              key={tab.key}
              tab={tab}
              active={tab.key === activeTab}
              onClick={() => setActiveTab(tab.key)}
            />
          ))}
        </div>

        <label
          className={`task-execution-nav__more${
            SUPPORTING_TABS.some((tab) => tab.key === activeTab)
              ? " is-active"
              : ""
          }`}
        >
          <span className="sr-only">Open another task view</span>
          <select
            className="task-execution-nav__more-select"
            aria-label="Open another task view"
            value={
              SUPPORTING_TABS.some((tab) => tab.key === activeTab)
                ? activeTab
                : ""
            }
            onChange={(event) => {
              if (event.target.value) setActiveTab(event.target.value);
            }}
          >
            <option value="">More tools</option>
            {SUPPORTING_TABS.map((tab) => (
              <option key={tab.key} value={tab.key}>
                {tab.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="task-execution-status-strip" aria-label="Task execution status">
        <ExecutionStatusBadge tone="warn">
          {humanizeDisplayValue(
            safeTask.priority_label || safeTask.priority,
            "Priority unknown"
          )}
        </ExecutionStatusBadge>
        <ExecutionStatusBadge tone="default">
          {humanizeDisplayValue(
            safeTask.status || safeTask.task_status,
            "Status unknown"
          )}
        </ExecutionStatusBadge>
        <ExecutionStatusBadge tone="neutral">
          {humanizeDisplayValue(
            safeTask.validation_status,
            "Validation unknown"
          )}
        </ExecutionStatusBadge>
        <ExecutionStatusBadge tone="neutral">
          {humanizeDisplayValue(
            workflowContext?.status || safeTask.workflow_status,
            "Not Linked"
          )}
        </ExecutionStatusBadge>
        <ExecutionStatusBadge tone="default">
          {activeTabConfig.label}
        </ExecutionStatusBadge>
      </div>
      {activeTab === "overview" ? (
        <TaskOverviewTab
          task={safeTask}
          onOpenTab={setActiveTab}
          intelligence={intelligence}
          intelligenceLoading={intelligenceLoading}
          intelligenceError={intelligenceError}
          intelligenceQuestion={intelligenceQuestion}
          onRequestIntelligence={onRequestIntelligence}
        />
      ) : null}
      {activeTab === "guidance" ? (
        <RemediationGuidanceTab
          task={safeTask}
          guidanceContext={remediationGuidanceContext}
          guidanceLoading={remediationGuidanceLoading}
          guidanceError={remediationGuidanceError}
          onRefreshGuidance={onRefreshRemediationGuidance}
          onOpenTab={setActiveTab}
        />
      ) : null}
      {activeTab === "execution_plan" ? (
        <ExecutionPlanTab
          task={safeTask}
          executionPlanContext={executionPlanContext}
          loading={executionPlanLoading}
          error={executionPlanError}
          onRefresh={onRefreshExecutionPlan}
        />
      ) : null}
      {activeTab === "learning" ? (
        <LearningInsightPanel
          task={safeTask}
          context={learningContext}
          loading={learningLoading}
          error={learningError}
          refreshState={learningRefreshState}
          onRefresh={onRefreshLearning}
        />
      ) : null}
      {activeTab === "optimization" ? (
        <OptimizationInsightPanel
          context={optimizationContext}
          task={safeTask}
          loading={optimizationLoading}
          error={optimizationError}
          onRefresh={onRefreshOptimization}
          onOpenTask={null}
        />
      ) : null}
      {activeTab === "workflow" ? (
        <WorkflowExecutionTab
          task={safeTask}
          workflowEvents={safeWorkflowEvents}
          workflowContext={workflowContext}
          workflowLoading={workflowLoading}
          workflowError={workflowError}
          onRefreshWorkflow={onRefreshWorkflow}
          onOpenTab={setActiveTab}
        />
      ) : null}
      {activeTab === "validation" ? <ValidationExecutionTab task={safeTask} validationEvents={safeValidationEvents} onRunValidation={onRunValidation} validationRunning={validationRunning} onOpenTab={setActiveTab} /> : null}
      {activeTab === "evidence" ? <EvidenceExecutionTab task={safeTask} evidenceRecords={safeEvidenceRecords} evidenceContext={evidenceContext} evidenceLoading={evidenceLoading} evidenceError={evidenceError} onRefreshEvidence={onRefreshEvidence} onOpenTab={setActiveTab} /> : null}
      {activeTab === "sop" ? (
        <SOPExecutionTab
          task={safeTask}
          sopContext={sopContext}
          sopLoading={sopLoading}
          sopError={sopError}
          onRefreshSOP={onRefreshSOP}
          onOpenTab={setActiveTab}
        />
      ) : null}
      {activeTab === "history" ? <HistoryExecutionTab task={safeTask} events={safeHistoryEvents} /> : null}
      {activeTab === "decision" ? <DecisionSupportTab task={safeTask} decision={decision} onRefreshDecision={onRefreshDecision} onFeedback={onDecisionFeedback} decisionLoading={decisionLoading} /> : null}
    </ExecutionModalShell>
  );
}
